import { useState, useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { useSEO, getContactPageSchema, getBreadcrumbSchema, getFAQSchema } from '../../utils/seo';

export default function ContactPage() {
  const [selectedInquiry, setSelectedInquiry] = useState('');
  const [inquiryDropdownOpen, setInquiryDropdownOpen] = useState(false);
  const [selectedMeetingType, setSelectedMeetingType] = useState('');
  const [meetingTypeDropdownOpen, setMeetingTypeDropdownOpen] = useState(false);
  const [selectedMeetingTime, setSelectedMeetingTime] = useState('');
  const [meetingTimeDropdownOpen, setMeetingTimeDropdownOpen] = useState(false);
  const [messageCharCount, setMessageCharCount] = useState(0);
  const [contactFormStatus, setContactFormStatus] = useState('');
  const [meetingFormStatus, setMeetingFormStatus] = useState('');

  // Detect if we're on Dutch version
  const isDutch = window.location.pathname.startsWith('/nl');

  // SEO Implementation
  useSEO({
    title: isDutch ? 'Contact Thyamis - Neem Contact Op voor IT-oplossingen & Ondersteuning' : 'Contact Thyamis - Get in Touch for IT Solutions & Support',
    description: isDutch ? 'Neem contact op met Thyamis voor professionele IT-oplossingen, technische ondersteuning of partnerschapsmogelijkheden. Bel +31 (0)616300011, e-mail sales@thyamis.com, of bezoek ons kantoor in Mijdrecht, Nederland.' : 'Contact Thyamis for professional IT solutions, technical support, or partnership opportunities. Call +31 (0)616300011, email sales@thyamis.com, or visit our office in Mijdrecht, Netherlands.',
    keywords: isDutch ? 'contact Thyamis, IT-ondersteuning, technische ondersteuning, IT-consultatie, partnerschapsmogelijkheden, Mijdrecht IT-diensten' : 'contact Thyamis, IT support, technical support, IT consultation, partnership opportunities, Mijdrecht IT services',
    canonical: isDutch ? '/nl/contact' : '/contact',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        getContactPageSchema(),
        getBreadcrumbSchema([
          { name: isDutch ? 'Home' : 'Home', url: isDutch ? '/nl' : '/' },
          { name: isDutch ? 'Contact' : 'Contact', url: isDutch ? '/nl/contact' : '/contact' },
        ]),
        getFAQSchema(isDutch ? [
          {
            question: 'Welke diensten biedt Thyamis aan?',
            answer: 'Thyamis biedt uitgebreide IT-oplossingen, waaronder hardware, software, clouddiensten, AI-implementatie, integratiediensten en beheerde IT-ondersteuning. Wij zijn gespecialiseerd in end-to-end technologieoplossingen voor bedrijven van elke omvang.',
          },
          {
            question: 'Hoe vraag ik een offerte aan voor diensten?',
            answer: 'U kunt een offerte aanvragen door ons contactformulier in te vullen, ons verkoopteam te bellen op +31 (0)616300011, of een consultatiegesprek in te plannen. Wij beoordelen uw behoeften en leveren binnen 24-48 uur een gedetailleerd voorstel.',
          },
          {
            question: 'Bedienen jullie internationale klanten?',
            answer: 'Ja, hoewel we gevestigd zijn in Nederland, bedienen we klanten in heel Europa en internationaal. We hebben ervaring met wereldwijde implementaties en kunnen wereldwijd externe ondersteuning en consultatiediensten bieden.',
          },
          {
            question: 'Wat zijn uw ondersteuningsuren?',
            answer: 'Onze standaard ondersteuningsuren zijn maandag-vrijdag van 8:00 tot 18:00 uur CET. We bieden ook 24/7 noodondersteuning voor kritieke problemen en hebben uitgebreide ondersteuningspakketten beschikbaar voor zakelijke klanten.',
          },
          {
            question: 'Hoe snel reageren jullie op ondersteuningsverzoeken?',
            answer: 'We reageren binnen 4 uur op kritieke problemen, binnen 8 uur op problemen met hoge prioriteit en binnen 24 uur op standaardverzoeken. Noodondersteuning is onmiddellijk beschikbaar voor bedrijfskritieke situaties.',
          },
          {
            question: 'Bieden jullie externe hulp aan?',
            answer: 'Ja, we bieden veilige externe hulp voor probleemoplossing, systeemconfiguratie en onderhoudstaken. Onze technici kunnen met uw toestemming op afstand toegang krijgen tot uw systemen om problemen snel en efficiënt op te lossen.',
          },
          {
            question: 'Bieden jullie volumekortingen aan?',
            answer: 'Ja, we bieden concurrerende volumekortingen voor bulkaankopen en zakelijke overeenkomsten. Neem contact op met ons verkoopteam om prijsopties te bespreken op basis van uw specifieke vereisten en volumebehoeften.',
          },
          {
            question: 'Hoe word ik partner?',
            answer: 'We verwelkomen partnerschapsmogelijkheden met wederverkopers, systeemintegrators en technologieleveranciers. Neem contact op met ons partnerteam via info@thyamis.com om samenwerkingsmogelijkheden en de voordelen van ons partnerprogramma te bespreken.',
          },
          {
            question: 'Welke betaalmethoden accepteren jullie?',
            answer: 'We accepteren grote creditcards (Visa, Mastercard), bankoverschrijvingen, PayPal, iDEAL en bieden flexibele betalingsvoorwaarden voor zakelijke klanten. We ondersteunen ook verschillende factureringsregelingen op basis van uw zakelijke behoeften.',
          },
          {
            question: 'Bieden jullie financieringsopties aan?',
            answer: 'Nee, we bieden momenteel geen financieringsopties en abonnementsmodellen voor doorlopende diensten aan. We overwegen echter wel financieringsopties in de toekomst. Neem contact op met ons verkoopteam voor meer informatie.',
          },
        ] : [
          {
            question: 'What services does Thyamis offer?',
            answer: 'Thyamis provides comprehensive IT solutions including hardware, software, cloud services, AI implementation, integration services, and managed IT support. We specialize in end-to-end technology solutions for businesses of all sizes.',
          },
          {
            question: 'How do I get a quote for services?',
            answer: 'You can request a quote by filling out our contact form, calling our sales team at +31 (0)616300011, or scheduling a consultation meeting. We will assess your needs and provide a detailed proposal within 24-48 hours.',
          },
          {
            question: 'Do you serve international clients?',
            answer: 'Yes, while we are based in the Netherlands, we serve clients across Europe and internationally. We have experience with global deployments and can provide remote support and consultation services worldwide.',
          },
          {
            question: 'What are your support hours?',
            answer: 'Our standard support hours are Monday-Friday 8:00 AM to 6:00 PM CET. We also offer 24/7 emergency support for critical issues and have extended support packages available for enterprise clients.',
          },
          {
            question: 'How quickly do you respond to support requests?',
            answer: 'We respond to critical issues within 4 hours, high-priority issues within 8 hours, and standard requests within 24 hours. Emergency support is available immediately for business-critical situations.',
          },
          {
            question: 'Do you provide remote assistance?',
            answer: 'Yes, we provide secure remote assistance for troubleshooting, system configuration, and maintenance tasks. Our technicians can remotely access your systems with your permission to resolve issues quickly and efficiently.',
          },
          {
            question: 'Do you offer volume discounts?',
            answer: 'Yes, we offer competitive volume discounts for bulk purchases and enterprise agreements. Contact our sales team to discuss pricing options based on your specific requirements and volume needs.',
          },
          {
            question: 'How do I become a partner?',
            answer: 'We welcome partnership opportunities with resellers, system integrators, and technology providers. Contact our partnership team at info@thyamis.com to discuss collaboration opportunities and our partner program benefits.',
          },
          {
            question: 'What payment methods do you accept?',
            answer: 'We accept major credit cards (Visa, Mastercard), bank transfers, PayPal, iDEAL and offer flexible payment terms for enterprise clients. We also support various invoicing arrangements based on your business needs.',
          },
          {
            question: 'Do you offer financing options?',
            answer: 'No, we do not offer financing options, and subscription-based models for ongoing services. However, we do consider financing options in the future. Please contact our sales team for more information.',
          },
        ]),
      ],
    },
  });

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const dateInput = document.getElementById('meeting-date') as HTMLInputElement;
    if (dateInput) {
      dateInput.setAttribute('min', today);
    }
  }, []);

  const handleInquirySelect = (value: string, label: string) => {
    setSelectedInquiry(label);
    (document.getElementById('inquiryType') as HTMLInputElement).value = value;
    setInquiryDropdownOpen(false);
  };

  const handleMeetingTypeSelect = (value: string, label: string) => {
    setSelectedMeetingType(label);
    (document.getElementById('meeting-type-select') as HTMLInputElement).value = value;
    setMeetingTypeDropdownOpen(false);
  };

  const handleMeetingTimeSelect = (value: string, label: string) => {
    setSelectedMeetingTime(label);
    (document.getElementById('meeting-time') as HTMLInputElement).value = value;
    setMeetingTimeDropdownOpen(false);
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageCharCount(e.target.value.length);
  };

  const toggleFAQ = (targetId: string) => {
    const element = document.getElementById(targetId);
    if (element) {
      element.classList.toggle('hidden');
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleContactFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new URLSearchParams(formData as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setContactFormStatus('success');
        form.reset();
        setSelectedInquiry('');
        setMessageCharCount(0);
        setTimeout(() => setContactFormStatus(''), 5000);
      } else {
        setContactFormStatus('error');
      }
    } catch (error) {
      setContactFormStatus('error');
    }
  };

  const handleMeetingFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new URLSearchParams(formData as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setMeetingFormStatus('success');
        form.reset();
        setSelectedMeetingType('');
        setSelectedMeetingTime('');
        setTimeout(() => setMeetingFormStatus(''), 5000);
      } else {
        setMeetingFormStatus('error');
      }
    } catch (error) {
      setMeetingFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-3 border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex items-center text-sm text-gray-600">
            <a href={isDutch ? "/nl" : "/"} className="hover:text-primary transition-colors">
              Home
            </a>
            <div className="w-4 h-4 flex items-center justify-center mx-1">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-primary">Contact</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4">
          <div className="w-full max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {isDutch ? 'Contact Thyamis' : 'Contact Thyamis'}
            </h1>
            <p className="text-lg text-gray-700 mb-8">
              {isDutch 
                ? 'We zijn er om u te helpen slagen. Neem contact op met ons expertteam voor gepersonaliseerde IT-oplossingen, technische ondersteuning of partnerschapsmogelijkheden.'
                : "We're here to help you succeed. Get in touch with our expert team for personalized IT solutions, technical support, or partnership opportunities."
              }
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="tel:+31616300011"
                className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium flex items-center hover:bg-primary/90 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-phone-line"></i>
                </div>
                {isDutch ? 'Bel Nu' : 'Call Now'}
              </a>
              <a
                href="mailto:sales@thyamis.com"
                className="bg-white border-2 border-gray-300 text-gray-800 px-6 py-3 rounded-button whitespace-nowrap font-medium flex items-center hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-mail-line"></i>
                </div>
                {isDutch ? 'E-mail Ons' : 'Email Us'}
              </a>
              <button
                onClick={() => scrollToSection('meeting-section')}
                className="bg-secondary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium flex items-center hover:bg-secondary/90 transition-colors cursor-pointer"
              >
                {isDutch ? 'Plan Afspraak' : 'Schedule Meeting'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Multiple Ways to Reach Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">
            {isDutch ? 'Meerdere Manieren om Ons te Bereiken' : 'Multiple Ways to Reach Us'}
          </h2>
          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
            {isDutch
              ? 'Kies de contactmethode die het beste bij u past. Ons team is beschikbaar via verschillende kanalen om de ondersteuning te bieden die u nodig heeft.'
              : 'Choose the contact method that works best for you. Our team is available through various channels to provide the support you need.'
            }
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Phone Support */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-white/80 backdrop-blur rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-phone-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-800">
                {isDutch ? 'Telefoonondersteuning' : 'Phone Support'}
              </h3>
              <div className="space-y-3 mb-4">
                <div>
                  <p className="font-medium text-gray-800">{isDutch ? 'Verkoopafdeling' : 'Sales Department'}</p>
                  <p className="text-gray-600">+31 (0)616300011</p>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {isDutch ? 'Maandag - Vrijdag: 8:00 - 18:00 uur CET' : 'Monday - Friday: 8:00 AM - 6:00 PM CET'}
              </p>
            </div>

            {/* Email Support */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-white/80 backdrop-blur rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-mail-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-800">
                {isDutch ? 'E-mailondersteuning' : 'Email Support'}
              </h3>
              <div className="space-y-3 mb-4">
                <div>
                  <p className="font-medium text-gray-800">{isDutch ? 'Verkoop & Partnerschappen' : 'Sales & Partnerships'}</p>
                  <p className="text-gray-600">sales@thyamis.com</p>
                </div>
                <div>
                  <p className="font-medium text-gray-800">{isDutch ? 'Algemene Informatie' : 'General Information'}</p>
                  <p className="text-gray-600">info@thyamis.com</p>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {isDutch ? 'Reactie binnen 24 uur' : 'Response within 24 hours'}
              </p>
            </div>

            {/* Office Locations */}
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-white/80 backdrop-blur rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-amber-600">
                  <i className="ri-map-pin-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-800">
                {isDutch ? 'Kantoorlocaties' : 'Office Locations'}
              </h3>
              <div className="space-y-3 mb-4">
                <div>
                  <p className="font-medium text-gray-800">{isDutch ? 'Hoofdkantoor' : 'Main Office'}</p>
                  <p className="text-gray-600">Energieweg 2, 3641RT, Mijdrecht</p>
                </div>
              </div>
              <button
                onClick={() => scrollToSection('office-locations')}
                className="text-amber-600 font-medium hover:underline cursor-pointer"
              >
                {isDutch ? 'Bekijk Locatie' : 'View Location'}
              </button>
            </div>

            {/* Emergency Support */}
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-white/80 backdrop-blur rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-teal-600">
                  <i className="ri-customer-service-2-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-800">
                {isDutch ? 'Noodondersteuning' : 'Emergency Support'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isDutch
                  ? '24/7 noodondersteuning voor kritieke problemen die uw bedrijfsvoering beïnvloeden.'
                  : '24/7 emergency support for critical issues affecting your business operations.'
                }
              </p>
              <div className="space-y-2">
                <p className="font-medium text-gray-800">{isDutch ? 'Noodlijn' : 'Emergency Hotline'}</p>
                <p className="text-gray-600">+31 (0)616300011</p>
                <p className="text-sm text-gray-600">
                  {isDutch ? '24/7 beschikbaar voor bestaande klanten' : 'Available 24/7 for existing customers'}
                </p>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-gradient-to-br from-rose-50 to-rose-100 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-white/80 backdrop-blur rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-rose-600">
                  <i className="ri-share-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-800">
                {isDutch ? 'Sociale Media' : 'Social Media'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isDutch
                  ? 'Volg ons op sociale media voor updates, tips en community-ondersteuning.'
                  : 'Connect with us on social media for updates, tips, and community support.'
                }
              </p>
              <div className="flex space-x-3">
                <a
                  href="https://www.linkedin.com/company/thyamis/about/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center text-blue-600">
                    <i className="ri-linkedin-fill"></i>
                  </div>
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center text-blue-400">
                    <i className="ri-twitter-x-fill"></i>
                  </div>
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center text-blue-700">
                    <i className="ri-facebook-fill"></i>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Send Us a Message */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-4">
              {isDutch ? 'Stuur Ons een Bericht' : 'Send Us a Message'}
            </h2>
            <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
              {isDutch
                ? 'Vul het onderstaande formulier in en we nemen zo snel mogelijk contact met u op. Ons team staat klaar om te helpen met eventuele vragen of wensen die u heeft.'
                : "Fill out the form below and we'll get back to you as soon as possible. Our team is ready to help with any questions or requirements you may have."
              }
            </p>
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Contact Form */}
              <div className="w-full lg:w-2/3 bg-white p-8 rounded-lg shadow-sm border border-gray-100">
                <form
                  id="contact-form"
                  data-readdy-form
                  action="https://readdy.ai/api/form/d51acptpfatkmn6ifci0"
                  method="post"
                  onSubmit={handleContactFormSubmit}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="firstName" className="block text-gray-700 font-medium mb-2">
                        {isDutch ? 'Voornaam' : 'First Name'}
                      </label>
                      <input
                        id="firstName"
                        name="firstName"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder={isDutch ? 'Jan' : 'John'}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-gray-700 font-medium mb-2">
                        {isDutch ? 'Achternaam' : 'Last Name'}
                      </label>
                      <input
                        id="lastName"
                        name="lastName"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder={isDutch ? 'Jansen' : 'Smith'}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                        {isDutch ? 'E-mailadres' : 'Email Address'}
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder={isDutch ? 'jan.jansen@bedrijf.nl' : 'john.smith@company.com'}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
                        {isDutch ? 'Telefoonnummer' : 'Phone Number'}
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="+31 6 1234 5678"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-gray-700 font-medium mb-2">
                        {isDutch ? 'Bedrijfsnaam' : 'Company Name'}
                      </label>
                      <input
                        id="company"
                        name="company"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder={isDutch ? 'Acme Bedrijf' : 'Acme Corporation'}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="inquiryType" className="block text-gray-700 font-medium mb-2">
                        {isDutch ? 'Type Aanvraag' : 'Inquiry Type'}
                      </label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setInquiryDropdownOpen(!inquiryDropdownOpen)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer"
                        >
                          <span className={selectedInquiry ? 'text-gray-900' : 'text-gray-500'}>
                            {selectedInquiry || (isDutch ? 'Selecteer type aanvraag' : 'Select inquiry type')}
                          </span>
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </button>
                        {inquiryDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg mt-1 shadow-lg z-10">
                            <div className="py-1">
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleInquirySelect('sales', isDutch ? 'Verkoop & Productinformatie' : 'Sales & Product Information')}
                              >
                                {isDutch ? 'Verkoop & Productinformatie' : 'Sales & Product Information'}
                              </button>
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleInquirySelect('support', isDutch ? 'Technische Ondersteuning' : 'Technical Support')}
                              >
                                {isDutch ? 'Technische Ondersteuning' : 'Technical Support'}
                              </button>
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleInquirySelect('partnership', isDutch ? 'Partnerschapsmogelijkheden' : 'Partnership Opportunities')}
                              >
                                {isDutch ? 'Partnerschapsmogelijkheden' : 'Partnership Opportunities'}
                              </button>
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleInquirySelect('general', isDutch ? 'Algemene Informatie' : 'General Information')}
                              >
                                {isDutch ? 'Algemene Informatie' : 'General Information'}
                              </button>
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleInquirySelect('billing', isDutch ? 'Facturering & Accounts' : 'Billing & Accounts')}
                              >
                                {isDutch ? 'Facturering & Accounts' : 'Billing & Accounts'}
                              </button>
                            </div>
                          </div>
                        )}
                        <input type="hidden" id="inquiryType" name="inquiryType" required />
                      </div>
                    </div>
                  </div>
                  <div className="mb-6">
                    <label htmlFor="subject" className="block text-gray-700 font-medium mb-2">
                      {isDutch ? 'Onderwerp' : 'Subject'}
                    </label>
                    <input
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder={isDutch ? 'Korte beschrijving van uw aanvraag' : 'Brief description of your inquiry'}
                      required
                    />
                  </div>
                  <div className="mb-6">
                    <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
                      {isDutch ? 'Bericht' : 'Message'}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder={isDutch ? 'Geef gedetailleerde informatie over uw wensen of vragen' : 'Please provide detailed information about your requirements or questions'}
                      onChange={handleMessageChange}
                      required
                    ></textarea>
                    <div className="text-sm text-gray-500 mt-1">
                      <span>{messageCharCount}</span>/500 {isDutch ? 'tekens' : 'characters'}
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="block text-gray-700 font-medium mb-2">
                      {isDutch ? 'Voorkeur Contactmethode' : 'Preferred Contact Method'}
                    </label>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="contactMethod"
                          value="email"
                          className="w-5 h-5 text-primary focus:ring-primary cursor-pointer"
                          required
                        />
                        <span className="ml-2">{isDutch ? 'E-mail' : 'Email'}</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="contactMethod"
                          value="phone"
                          className="w-5 h-5 text-primary focus:ring-primary cursor-pointer"
                          required
                        />
                        <span className="ml-2">{isDutch ? 'Telefoon' : 'Phone'}</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="contactMethod"
                          value="video"
                          className="w-5 h-5 text-primary focus:ring-primary cursor-pointer"
                          required
                        />
                        <span className="ml-2">{isDutch ? 'Videogesprek' : 'Video Call'}</span>
                      </label>
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="block text-gray-700 font-medium mb-2">
                      {isDutch ? 'Privacy Toestemming' : 'Privacy Consent'}
                    </label>
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        id="privacy-consent"
                        name="privacyConsent"
                        className="w-5 h-5 text-primary focus:ring-primary mt-0.5 cursor-pointer"
                        required
                      />
                      <span className="ml-3 text-gray-700">
                        {isDutch ? (
                          <>
                            Ik ga akkoord met het{' '}
                            <a href="/nl/privacy-policy" className="text-primary hover:underline">
                              Privacybeleid
                            </a>{' '}
                            en stem in met de verwerking van mijn persoonlijke gegevens met als doel het beantwoorden van mijn aanvraag.
                          </>
                        ) : (
                          <>
                            I agree to the{' '}
                            <a href="/privacy-policy" className="text-primary hover:underline">
                              Privacy Policy
                            </a>{' '}
                            and consent to the processing of my personal data for the purpose of responding to my inquiry.
                          </>
                        )}
                      </span>
                    </label>
                  </div>
                  {contactFormStatus === 'success' && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
                      {isDutch
                        ? 'Bedankt! Uw bericht is succesvol verzonden. We nemen binnenkort contact met u op.'
                        : "Thank you! Your message has been sent successfully. We'll get back to you soon."
                      }
                    </div>
                  )}
                  {contactFormStatus === 'error' && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                      {isDutch
                        ? 'Sorry, er is een fout opgetreden bij het verzenden van uw bericht. Probeer het opnieuw.'
                        : 'Sorry, there was an error sending your message. Please try again.'
                      }
                    </div>
                  )}
                  <button
                    type="submit"
                    className="bg-primary text-white px-8 py-3 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer"
                  >
                    {isDutch ? 'Verstuur Bericht' : 'Send Message'}
                  </button>
                </form>
              </div>

              {/* Sidebar */}
              <div className="w-full lg:w-1/3 space-y-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                  <h3 className="text-xl font-semibold mb-4">
                    {isDutch ? 'Openingstijden' : 'Business Hours'}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{isDutch ? 'Maandag - Vrijdag' : 'Monday - Friday'}</span>
                      <span className="font-medium">{isDutch ? '8:00 - 18:00' : '8:00 AM - 6:00 PM'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{isDutch ? 'Zaterdag' : 'Saturday'}</span>
                      <span className="font-medium">{isDutch ? 'Gesloten' : 'Closed'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{isDutch ? 'Zondag' : 'Sunday'}</span>
                      <span className="font-medium">{isDutch ? 'Gesloten' : 'Closed'}</span>
                    </div>
                    <div className="border-t pt-3 mt-3">
                      <p className="text-sm text-gray-600">
                        {isDutch
                          ? 'Alle tijden zijn Midden-Europese Tijd (CET)'
                          : 'All times are Central European Time (CET)'
                        }
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                  <h3 className="text-xl font-semibold mb-4">
                    {isDutch ? 'Reactietijden' : 'Response Times'}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                        <div className="w-4 h-4 flex items-center justify-center text-green-600">
                          <i className="ri-mail-line"></i>
                        </div>
                      </div>
                      <div>
                        <p className="font-medium">{isDutch ? 'E-mailaanvragen' : 'Email Inquiries'}</p>
                        <p className="text-sm text-gray-600">{isDutch ? 'Binnen 24 uur' : 'Within 24 hours'}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <div className="w-4 h-4 flex items-center justify-center text-blue-600">
                          <i className="ri-phone-line"></i>
                        </div>
                      </div>
                      <div>
                        <p className="font-medium">{isDutch ? 'Telefoongesprekken' : 'Phone Calls'}</p>
                        <p className="text-sm text-gray-600">
                          {isDutch ? 'Direct tijdens openingstijden' : 'Immediate during business hours'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section id="office-locations" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">
            {isDutch ? 'Kantoorlocaties' : 'Office Locations'}
          </h2>
          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
            {isDutch
              ? 'Bezoek ons op onze handige locatie of neem rechtstreeks contact op met ons lokale team voor persoonlijke service en ondersteuning.'
              : 'Visit us at our convenient location or contact our local team directly for personalized service and support.'
            }
          </p>
          <div className="max-w-6xl mx-auto">
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div className="order-2 lg:order-1">
                  <div className="w-full h-80 lg:h-96">
                    <img
                      src="https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/266ec756f8eb72cde0a34e59a5aaed51.png"
                      alt={isDutch ? 'Thyamis Kantoorgebouw' : 'Thyamis Office Building'}
                      className="w-full h-full object-cover object-center rounded-lg shadow-sm"
                    />
                  </div>
                </div>
                <div className="flex flex-col justify-between h-full order-1 lg:order-2">
                  <div>
                    <div className="flex items-start space-x-4 mb-8">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <div className="w-6 h-6 flex items-center justify-center text-primary">
                          <i className="ri-building-line"></i>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold mb-3 text-gray-800">
                          {isDutch ? 'Thyamis Kantoor' : 'Thyamis Office'}
                        </h3>
                        <div className="space-y-2">
                          <p className="text-gray-600 font-medium">{isDutch ? 'Adres:' : 'Address:'}</p>
                          <p className="text-gray-700">Energieweg 2</p>
                          <p className="text-gray-700">3641RT Mijdrecht</p>
                          <p className="text-gray-700">{isDutch ? 'Nederland' : 'Netherlands'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                      <h4 className="font-semibold mb-4 text-gray-800 flex items-center">
                        <div className="w-5 h-5 flex items-center justify-center mr-2 text-primary">
                          <i className="ri-contacts-line"></i>
                        </div>
                        {isDutch ? 'Contactinformatie' : 'Contact Information'}
                      </h4>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mr-3">
                              <div className="w-4 h-4 flex items-center justify-center text-primary">
                                <i className="ri-phone-line"></i>
                              </div>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500 font-medium">{isDutch ? 'Telefoon' : 'Phone'}</p>
                              <p className="text-gray-700 font-medium">+31 (0)616300011</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mr-3">
                              <div className="w-4 h-4 flex items-center justify-center text-primary">
                                <i className="ri-mail-line"></i>
                              </div>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500 font-medium">{isDutch ? 'E-mail' : 'Email'}</p>
                              <p className="text-gray-700 font-medium">sales@thyamis.com</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 mt-6">
                    <a
                      href="https://maps.google.com/?q=Energieweg+2,+3641RT+Mijdrecht,+Netherlands"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium text-sm flex items-center justify-center hover:bg-primary/90 transition-colors cursor-pointer"
                    >
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-direction-line"></i>
                      </div>
                      {isDutch ? 'Routebeschrijving' : 'Get Directions'}
                    </a>
                    <a
                      href="tel:+31616300011"
                      className="flex-1 bg-white border-2 border-primary text-primary px-6 py-3 rounded-button whitespace-nowrap font-medium text-sm flex items-center justify-center hover:bg-primary hover:text-white transition-colors cursor-pointer"
                    >
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-phone-line"></i>
                      </div>
                      {isDutch ? 'Bel Kantoor' : 'Call Office'}
                    </a>
                  </div>
                </div>
              </div>
              <div className="bg-gray-100 rounded-lg overflow-hidden relative">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2445.234567890123!2d4.859428!3d52.205555!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c5e9b7b9b7b9b7%3A0x7b7b7b7b7b7b7b7b!2sEnergieweg%202%2C%203641%20RT%20Mijdrecht%2C%20Netherlands!5e0!3m2!1sen!2snl!4v1635123456789!5m2!1sen!2snl&q=Energieweg+2,+3641RT+Mijdrecht,+Netherlands"
                  width="100%"
                  height="400"
                  title={isDutch ? 'Thyamis Kantoorlocatie - Energieweg 2, 3641RT Mijdrecht, Nederland' : 'Thyamis Office Location - Energieweg 2, 3641RT Mijdrecht, Netherlands'}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="border-0"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule a Meeting */}
      <section id="meeting-section" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">
            {isDutch ? 'Plan een Afspraak' : 'Schedule a Meeting'}
          </h2>
          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
            {isDutch
              ? 'Boek een persoonlijke consultatie met onze experts om uw specifieke wensen te bespreken en te ontdekken hoe Thyamis uw bedrijf kan helpen slagen.'
              : 'Book a personalized consultation with our experts to discuss your specific requirements and explore how Thyamis can help your business succeed.'
            }
          </p>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Available Meeting Types */}
              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold mb-6">
                  {isDutch ? 'Beschikbare Afspraaktypes' : 'Available Meeting Types'}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors">
                    <div className="w-6 h-6 flex items-center justify-center text-primary mt-1">
                      <i className="ri-chat-1-line"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold">
                        {isDutch ? 'Gratis Consultatie (30 min)' : 'Free Consultation (30 min)'}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {isDutch
                          ? 'Bespreek uw IT-behoeften en verken mogelijke oplossingen met onze experts.'
                          : 'Discuss your IT needs and explore potential solutions with our experts.'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors">
                    <div className="w-6 h-6 flex items-center justify-center text-primary mt-1">
                      <i className="ri-presentation-line"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold">
                        {isDutch ? 'Productdemo (45 min)' : 'Product Demo (45 min)'}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {isDutch
                          ? 'Zie onze oplossingen in actie met een gepersonaliseerde demonstratie.'
                          : 'See our solutions in action with a personalized demonstration.'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors">
                    <div className="w-6 h-6 flex items-center justify-center text-primary mt-1">
                      <i className="ri-tools-line"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold">
                        {isDutch ? 'Technische Review (60 min)' : 'Technical Review (60 min)'}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {isDutch
                          ? 'Diepgaande technische bespreking over integratie en implementatie.'
                          : 'In-depth technical discussion about integration and implementation.'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors">
                    <div className="w-6 h-6 flex items-center justify-center text-primary mt-1">
                      <i className="ri-lightbulb-line"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold">
                        {isDutch ? 'Strategiesessie (90 min)' : 'Strategy Session (90 min)'}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {isDutch
                          ? 'Uitgebreide planningssessie voor digitale transformatie-initiatieven.'
                          : 'Comprehensive planning session for digital transformation initiatives.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Book Your Meeting Form */}
              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold mb-6">
                  {isDutch ? 'Boek Uw Afspraak' : 'Book Your Meeting'}
                </h3>
                <form
                  id="meeting-form"
                  data-readdy-form
                  action="https://readdy.ai/api/form/d51acptpfatkmn6ifchg"
                  method="post"
                  onSubmit={handleMeetingFormSubmit}
                >
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="meeting-name" className="block text-gray-700 font-medium mb-2">
                        {isDutch ? 'Volledige Naam' : 'Full Name'}
                      </label>
                      <input
                        id="meeting-name"
                        name="name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder={isDutch ? 'Uw volledige naam' : 'Your full name'}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="meeting-email" className="block text-gray-700 font-medium mb-2">
                        {isDutch ? 'E-mailadres' : 'Email Address'}
                      </label>
                      <input
                        type="email"
                        id="meeting-email"
                        name="email"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder={isDutch ? 'uw.email@bedrijf.nl' : 'your.email@company.com'}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="meeting-company" className="block text-gray-700 font-medium mb-2">
                        {isDutch ? 'Bedrijfsnaam' : 'Company Name'}
                      </label>
                      <input
                        id="meeting-company"
                        name="company"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder={isDutch ? 'Uw bedrijfsnaam' : 'Your company name'}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="meeting-type-select" className="block text-gray-700 font-medium mb-2">
                        {isDutch ? 'Afspraaktype' : 'Meeting Type'}
                      </label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setMeetingTypeDropdownOpen(!meetingTypeDropdownOpen)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer"
                        >
                          <span className={selectedMeetingType ? 'text-gray-900' : 'text-gray-500'}>
                            {selectedMeetingType || (isDutch ? 'Selecteer afspraaktype' : 'Select meeting type')}
                          </span>
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </button>
                        {meetingTypeDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg mt-1 shadow-lg z-10">
                            <div className="py-1">
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() =>
                                  handleMeetingTypeSelect('consultation', isDutch ? 'Gratis Consultatie (30 min)' : 'Free Consultation (30 min)')
                                }
                              >
                                {isDutch ? 'Gratis Consultatie (30 min)' : 'Free Consultation (30 min)'}
                              </button>
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleMeetingTypeSelect('demo', isDutch ? 'Productdemo (45 min)' : 'Product Demo (45 min)')}
                              >
                                {isDutch ? 'Productdemo (45 min)' : 'Product Demo (45 min)'}
                              </button>
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleMeetingTypeSelect('technical', isDutch ? 'Technische Review (60 min)' : 'Technical Review (60 min)')}
                              >
                                {isDutch ? 'Technische Review (60 min)' : 'Technical Review (60 min)'}
                              </button>
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleMeetingTypeSelect('strategy', isDutch ? 'Strategiesessie (90 min)' : 'Strategy Session (90 min)')}
                              >
                                {isDutch ? 'Strategiesessie (90 min)' : 'Strategy Session (90 min)'}
                              </button>
                            </div>
                          </div>
                        )}
                        <input type="hidden" id="meeting-type-select" name="meetingType" required />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="meeting-date" className="block text-gray-700 font-medium mb-2">
                        {isDutch ? 'Voorkeursdatum' : 'Preferred Date'}
                      </label>
                      <input
                        type="date"
                        id="meeting-date"
                        name="date"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="meeting-time" className="block text-gray-700 font-medium mb-2">
                        {isDutch ? 'Voorkeurstijd' : 'Preferred Time'}
                      </label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setMeetingTimeDropdownOpen(!meetingTimeDropdownOpen)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer"
                        >
                          <span className={selectedMeetingTime ? 'text-gray-900' : 'text-gray-500'}>
                            {selectedMeetingTime || (isDutch ? 'Selecteer tijdslot' : 'Select time slot')}
                          </span>
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </button>
                        {meetingTimeDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg mt-1 shadow-lg z-10 max-h-48 overflow-y-auto">
                            <div className="py-1">
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleMeetingTimeSelect('09:00', '09:00')}
                              >
                                09:00
                              </button>
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleMeetingTimeSelect('10:00', '10:00')}
                              >
                                10:00
                              </button>
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleMeetingTimeSelect('11:00', '11:00')}
                              >
                                11:00
                              </button>
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleMeetingTimeSelect('13:00', '13:00')}
                              >
                                13:00
                              </button>
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleMeetingTimeSelect('14:00', '14:00')}
                              >
                                14:00
                              </button>
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleMeetingTimeSelect('15:00', '15:00')}
                              >
                                15:00
                              </button>
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleMeetingTimeSelect('16:00', '16:00')}
                              >
                                16:00
                              </button>
                            </div>
                          </div>
                        )}
                        <input type="hidden" id="meeting-time" name="time" required />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="meeting-notes" className="block text-gray-700 font-medium mb-2">
                        {isDutch ? 'Extra Opmerkingen (Optioneel)' : 'Additional Notes (Optional)'}
                      </label>
                      <textarea
                        id="meeting-notes"
                        name="notes"
                        rows={3}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder={isDutch ? 'Specifieke onderwerpen of vragen die u wilt bespreken' : "Any specific topics or questions you'd like to discuss"}
                      ></textarea>
                    </div>
                    {meetingFormStatus === 'success' && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
                        {isDutch
                          ? 'Bedankt! Uw afspraak is ingepland. We sturen u binnenkort een bevestigingsmail.'
                          : "Thank you! Your meeting has been scheduled. We'll send you a confirmation email shortly."
                        }
                      </div>
                    )}
                    {meetingFormStatus === 'error' && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                        {isDutch
                          ? 'Sorry, er is een fout opgetreden bij het inplannen van uw afspraak. Probeer het opnieuw.'
                          : 'Sorry, there was an error scheduling your meeting. Please try again.'
                        }
                      </div>
                    )}
                    <button
                      type="submit"
                      className="w-full bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer"
                    >
                      {isDutch ? 'Plan Afspraak' : 'Schedule Meeting'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">
            {isDutch ? 'Veelgestelde Vragen' : 'Frequently Asked Questions'}
          </h2>
          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
            {isDutch
              ? 'Vind snelle antwoorden op veelgestelde vragen over onze diensten, ondersteuning en processen. Kunt u niet vinden wat u zoekt? Neem rechtstreeks contact met ons op.'
              : "Find quick answers to common questions about our services, support, and processes. Can't find what you're looking for? Contact us directly."
            }
          </p>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* General Inquiries */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-primary mb-4">
                  {isDutch ? 'Algemene Vragen' : 'General Inquiries'}
                </h3>
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <button
                      onClick={() => toggleFAQ('faq-1')}
                      className="w-full text-left font-medium flex items-center justify-between cursor-pointer"
                    >
                      {isDutch ? 'Welke diensten biedt Thyamis aan?' : 'What services does Thyamis offer?'}
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </button>
                    <div id="faq-1" className="hidden mt-3 text-gray-600 text-sm">
                      {isDutch
                        ? 'Thyamis biedt uitgebreide IT-oplossingen, waaronder hardware, software, clouddiensten, AI-implementatie, integratiediensten en beheerde IT-ondersteuning. Wij zijn gespecialiseerd in end-to-end technologieoplossingen voor bedrijven van elke omvang.'
                        : 'Thyamis provides comprehensive IT solutions including hardware, software, cloud services, AI implementation, integration services, and managed IT support. We specialize in end-to-end technology solutions for businesses of all sizes.'
                      }
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <button
                      onClick={() => toggleFAQ('faq-2')}
                      className="w-full text-left font-medium flex items-center justify-between cursor-pointer"
                    >
                      {isDutch ? 'Hoe vraag ik een offerte aan voor diensten?' : 'How do I get a quote for services?'}
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </button>
                    <div id="faq-2" className="hidden mt-3 text-gray-600 text-sm">
                      {isDutch
                        ? 'U kunt een offerte aanvragen door ons contactformulier in te vullen, ons verkoopteam te bellen op +31 (0)616300011, of een consultatiegesprek in te plannen. Wij beoordelen uw behoeften en leveren binnen 24-48 uur een gedetailleerd voorstel.'
                        : "You can request a quote by filling out our contact form, calling our sales team at +31 (0)616300011, or scheduling a consultation meeting. We'll assess your needs and provide a detailed proposal within 24-48 hours."
                      }
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <button
                      onClick={() => toggleFAQ('faq-3')}
                      className="w-full text-left font-medium flex items-center justify-between cursor-pointer"
                    >
                      {isDutch ? 'Bedienen jullie internationale klanten?' : 'Do you serve international clients?'}
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </button>
                    <div id="faq-3" className="hidden mt-3 text-gray-600 text-sm">
                      {isDutch
                        ? 'Ja, hoewel we gevestigd zijn in Nederland, bedienen we klanten in heel Europa en internationaal. We hebben ervaring met wereldwijde implementaties en kunnen wereldwijd externe ondersteuning en consultatiediensten bieden.'
                        : "Yes, while we're based in the Netherlands, we serve clients across Europe and internationally. We have experience with global deployments and can provide remote support and consultation services worldwide."
                      }
                    </div>
                  </div>
                </div>
              </div>

              {/* Technical Support */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-primary mb-4">
                  {isDutch ? 'Technische Ondersteuning' : 'Technical Support'}
                </h3>
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <button
                      onClick={() => toggleFAQ('faq-4')}
                      className="w-full text-left font-medium flex items-center justify-between cursor-pointer"
                    >
                      {isDutch ? 'Wat zijn uw ondersteuningsuren?' : 'What are your support hours?'}
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </button>
                    <div id="faq-4" className="hidden mt-3 text-gray-600 text-sm">
                      {isDutch
                        ? 'Onze standaard ondersteuningsuren zijn maandag-vrijdag van 8:00 tot 18:00 uur CET. We bieden ook 24/7 noodondersteuning voor kritieke problemen en hebben uitgebreide ondersteuningspakketten beschikbaar voor zakelijke klanten.'
                        : 'Our standard support hours are Monday-Friday 8:00 AM to 6:00 PM CET. We also offer 24/7 emergency support for critical issues and have extended support packages available for enterprise clients.'
                      }
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <button
                      onClick={() => toggleFAQ('faq-5')}
                      className="w-full text-left font-medium flex items-center justify-between cursor-pointer"
                    >
                      {isDutch ? 'Hoe snel reageren jullie op ondersteuningsverzoeken?' : 'How quickly do you respond to support requests?'}
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </button>
                    <div id="faq-5" className="hidden mt-3 text-gray-600 text-sm">
                      {isDutch
                        ? 'We reageren binnen 4 uur op kritieke problemen, binnen 8 uur op problemen met hoge prioriteit en binnen 24 uur op standaardverzoeken. Noodondersteuning is onmiddellijk beschikbaar voor bedrijfskritieke situaties.'
                        : 'We respond to critical issues within 4 hours, high-priority issues within 8 hours, and standard requests within 24 hours. Emergency support is available immediately for business-critical situations.'
                      }
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <button
                      onClick={() => toggleFAQ('faq-6')}
                      className="w-full text-left font-medium flex items-center justify-between cursor-pointer"
                    >
                      {isDutch ? 'Bieden jullie externe hulp aan?' : 'Do you provide remote assistance?'}
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </button>
                    <div id="faq-6" className="hidden mt-3 text-gray-600 text-sm">
                      {isDutch
                        ? 'Ja, we bieden veilige externe hulp voor probleemoplossing, systeemconfiguratie en onderhoudstaken. Onze technici kunnen met uw toestemming op afstand toegang krijgen tot uw systemen om problemen snel en efficiënt op te lossen.'
                        : 'Yes, we provide secure remote assistance for troubleshooting, system configuration, and maintenance tasks. Our technicians can remotely access your systems with your permission to resolve issues quickly and efficiently.'
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
              {/* Sales & Partnerships */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-primary mb-4">
                  {isDutch ? 'Verkoop & Partnerschappen' : 'Sales & Partnerships'}
                </h3>
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <button
                      onClick={() => toggleFAQ('faq-7')}
                      className="w-full text-left font-medium flex items-center justify-between cursor-pointer"
                    >
                      {isDutch ? 'Bieden jullie volumekortingen aan?' : 'Do you offer volume discounts?'}
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </button>
                    <div id="faq-7" className="hidden mt-3 text-gray-600 text-sm">
                      {isDutch
                        ? 'Ja, we bieden concurrerende volumekortingen voor bulkaankopen en zakelijke overeenkomsten. Neem contact op met ons verkoopteam om prijsopties te bespreken op basis van uw specifieke vereisten en volumebehoeften.'
                        : 'Yes, we offer competitive volume discounts for bulk purchases and enterprise agreements. Contact our sales team to discuss pricing options based on your specific requirements and volume needs.'
                      }
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <button
                      onClick={() => toggleFAQ('faq-8')}
                      className="w-full text-left font-medium flex items-center justify-between cursor-pointer"
                    >
                      {isDutch ? 'Hoe word ik partner?' : 'How do I become a partner?'}
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </button>
                    <div id="faq-8" className="hidden mt-3 text-gray-600 text-sm">
                      {isDutch
                        ? 'We verwelkomen partnerschapsmogelijkheden met wederverkopers, systeemintegrators en technologieleveranciers. Neem contact op met ons partnerteam via info@thyamis.com om samenwerkingsmogelijkheden en de voordelen van ons partnerprogramma te bespreken.'
                        : 'We welcome partnership opportunities with resellers, system integrators, and technology providers. Contact our partnership team at info@thyamis.com to discuss collaboration opportunities and our partner program benefits.'
                      }
                    </div>
                  </div>
                </div>
              </div>

              {/* Billing & Accounts */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-primary mb-4">
                  {isDutch ? 'Facturering & Accounts' : 'Billing & Accounts'}
                </h3>
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <button
                      onClick={() => toggleFAQ('faq-9')}
                      className="w-full text-left font-medium flex items-center justify-between cursor-pointer"
                    >
                      {isDutch ? 'Welke betaalmethoden accepteren jullie?' : 'What payment methods do you accept?'}
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </button>
                    <div id="faq-9" className="hidden mt-3 text-gray-600 text-sm">
                      {isDutch
                        ? 'We accepteren grote creditcards (Visa, Mastercard), bankoverschrijvingen, PayPal, iDEAL en bieden flexibele betalingsvoorwaarden voor zakelijke klanten. We ondersteunen ook verschillende factureringsregelingen op basis van uw zakelijke behoeften.'
                        : 'We accept major credit cards (Visa, Mastercard), bank transfers, PayPal, iDEAL and offer flexible payment terms for enterprise clients. We also support various invoicing arrangements based on your business needs.'
                      }
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <button
                      onClick={() => toggleFAQ('faq-10')}
                      className="w-full text-left font-medium flex items-center justify-between cursor-pointer"
                    >
                      {isDutch ? 'Bieden jullie financieringsopties aan?' : 'Do you offer financing options?'}
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </button>
                    <div id="faq-10" className="hidden mt-3 text-gray-600 text-sm">
                      {isDutch
                        ? 'Nee, we bieden momenteel geen financieringsopties en abonnementsmodellen voor doorlopende diensten aan. We overwegen echter wel financieringsopties in de toekomst. Neem contact op met ons verkoopteam voor meer informatie.'
                        : "No, we don't offer financing options, and subscription-based models for ongoing services. However, we do consider financing options in the future. Please contact our sales team for more information."
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
